import { api } from './api';

export interface Notification {
  notificationId: string;
  userId: string;
  category: string;
  title: string;
  body: string;
  data: Record<string, unknown>;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsResponse {
  data: Notification[];
  nextCursor: string | null;
}

export interface UnreadCountResponse {
  count: number;
}

export interface CategoryPreference {
  category: string;
  title: string;
  description: string;
  email: boolean;
  inapp: boolean;
}

export interface PreferencesResponse {
  globalUnsubscribe: boolean;
  categories: CategoryPreference[];
  updatedAt: string;
}

export interface UpdatePreferencesRequest {
  globalUnsubscribe?: boolean;
  categories?: Record<string, { email: boolean; inapp: boolean }>;
}

/**
 * Get paginated notifications
 */
export async function getNotifications(
  token: string,
  cursor?: string,
  limit: number = 20,
): Promise<NotificationsResponse> {
  const params = new URLSearchParams();
  if (cursor) params.append('cursor', cursor);
  params.append('limit', limit.toString());

  return api.get<NotificationsResponse>(`/notifications-v2?${params.toString()}`, { token });
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(token: string): Promise<UnreadCountResponse> {
  return api.get<UnreadCountResponse>('/notifications-v2/unread-count', { token });
}

/**
 * Mark a notification as read
 */
export async function markAsRead(token: string, notificationId: string): Promise<void> {
  await api.patch(`/notifications-v2/${notificationId}/read`, {}, { token });
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(token: string): Promise<{ markedCount: number }> {
  return api.post<{ markedCount: number }>('/notifications-v2/read-all', {}, { token });
}

/**
 * Get notification preferences
 */
export async function getPreferences(token: string): Promise<PreferencesResponse> {
  return api.get<PreferencesResponse>('/notification-preferences', { token });
}

/**
 * Update notification preferences
 */
export async function updatePreferences(
  token: string,
  data: UpdatePreferencesRequest,
): Promise<PreferencesResponse> {
  return api.put<PreferencesResponse>('/notification-preferences', data, { token });
}
